import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Button } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import axios from "axios";
import Swal from "sweetalert2";
import MarketPlaceModal from "./MarketPlaceModal";

const decodeEntities = (html) => {
    if (!html) return "";
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

const decodeDescription = (html) => {
    if (!html) return "";

    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    let decoded = textarea.value;

    textarea.innerHTML = decoded;
    decoded = textarea.value;

    decoded = decoded.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, "$1");

    return decoded
        .replace(/&nbsp;/gi, " ")
        .replace(/\b([A-Z])\s{2,}([a-z])/g, "$1$2")
        .replace(/\s{2,}/g, " ")
        .trim();
};

const htmlToText = (html) => {
    if (!html) return "";

    const div = document.createElement("div");
    div.innerHTML = html;
    const text = div.textContent || div.innerText || "";

    return text
        .replace(/\b([A-Z])\s{2,}([a-z])/g, "$1$2")
        .replace(/\s{2,}/g, " ")
        .trim();
};

const isEmptyValue = (value) =>
    value === null || value === undefined || String(value).trim() === "";

const normalizeAttributes = (attributes) => {
    const entries = Array.isArray(attributes)
        ? attributes.map((attribute) => [attribute?.name, attribute?.value])
        : attributes && typeof attributes === "object"
            ? Object.entries(attributes).map(([name, value]) => [
                value && typeof value === "object" ? value.name ?? name : name,
                value && typeof value === "object" ? value.value : value,
            ])
            : [];

    return entries
        .filter(([name]) => name !== null && name !== undefined && String(name).trim())
        .map(([name, value]) => ({
            name: String(name),
            value: value === null || value === undefined || String(value).trim() === ""
                ? "—"
                : typeof value === "object"
                    ? value.name || value.title || "—"
                    : String(value),
        }));
};

const copyToClipboard = async (value) => {
    if (isEmptyValue(value)) return;

    try {
        await navigator.clipboard.writeText(String(value));
        Swal.fire({
            icon: "success",
            title: "Copied!",
            text: "Copied to clipboard",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
        });
    } catch (error) {
        try {
            const textarea = document.createElement("textarea");
            textarea.value = String(value);
            textarea.style.position = "fixed";
            textarea.style.left = "-9999px";
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            alert("Copied to clipboard");
        } catch (fallbackError) {
            alert("Unable to copy");
        }
    }
};

const CopyButton = ({ value }) => (
    <button
        type="button"
        className="copy-btn"
        onClick={() => copyToClipboard(value)}
        disabled={isEmptyValue(value)}
        title="Copy"
    >
        <i className="material-icons">content_copy</i>
    </button>
);

export default function MplacerProductView() {
    const { t } = useContext(TranslatorContext);
    const location = useLocation();
    const navigate = useNavigate();

    const passedItem = location.state?.item;

    const [selectedImage, setSelectedImage] = useState(passedItem?.card_img);
    const [showMarketModal, setShowMarketModal] = useState(false);
    const [assignedMarketPlaces, setAssignedMarketPlaces] = useState([]);

    const allImages = useMemo(
        () =>
            [passedItem?.card_img, ...(passedItem?.images?.map((img) => img.image) || [])].filter(
                Boolean
            ),
        [passedItem]
    );

    const fetchMarketPlaces = useCallback(async () => {
        if (!passedItem?.id) return;
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/market-placer/products-market-places/${passedItem.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            if (res.data?.data) {
                setAssignedMarketPlaces(res.data.data);
            }
        } catch (err) {
            console.error(err);
        }
    }, [passedItem?.id]);

    useEffect(() => {
        fetchMarketPlaces();
    }, [fetchMarketPlaces]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            if (showMarketModal || allImages.length < 2) return;

            const tag = e.target.tagName;
            if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target.isContentEditable) return;

            e.preventDefault();

            setSelectedImage((current) => {
                const total = allImages.length;
                const index = Math.max(allImages.indexOf(current), 0);
                const nextIndex =
                    e.key === "ArrowRight" ? (index + 1) % total : (index - 1 + total) % total;
                return allImages[nextIndex];
            });
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [allImages, showMarketModal]);

    const downloadImage = useCallback(
        async (imgPath) => {
            if (!imgPath) return;

            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/market-placer/download-image`,
                    {
                        params: { path: imgPath },
                        responseType: "blob",
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        },
                    }
                );

                const extension = imgPath.split(".").pop()?.split("?")[0] || "jpg";
                const code = passedItem?.product_code || "image";
                const index = allImages.indexOf(imgPath);
                const suffix = allImages.length > 1 && index >= 0 ? `-${index + 1}` : "";
                const fileName = `${code}${suffix}.${extension}`;

                const objectUrl = window.URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = objectUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(objectUrl);
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "Download failed",
                    text: "Unable to download this image.",
                });
            }
        },
        [passedItem?.product_code, allImages]
    );

    const currentImage = selectedImage || passedItem?.card_img;
    const productAttributes = normalizeAttributes(passedItem?.attributes);

    return (
        <PageLayout>
            <div className="mc-card mb-4 d-flex align-items-center justify-content-between">
                <div className="mc-breadcrumb">
                    <h3 className="mc-breadcrumb-title">{t("product_view")}</h3>

                    <ul className="mc-breadcrumb-list">
                        <li className="mc-breadcrumb-item">
                            <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                        </li>
                        <li className="mc-breadcrumb-item">
                            <Link to="#" className="mc-breadcrumb-link">{t("products")}</Link>
                        </li>
                        <li className="mc-breadcrumb-item">{t("product_view")}</li>
                    </ul>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <Button
                        variant="primary"
                        className="d-flex align-items-center gap-1"
                        onClick={() => setShowMarketModal(true)}
                    >
                        <i className="material-icons">add</i>
                        Add Marketplace
                    </Button>

                    <Button
                        variant="primary"
                        className="d-flex align-items-center gap-1"
                        onClick={() => navigate("/mplacer-products?tab=new")}
                    >
                        <i className="material-icons">check</i>
                        Listing Done
                    </Button>
                </div>
            </div>

            <div className="mc-card p-lg-4">
                <Row>
                    <Col xl={5}>
                        <h6 className="mc-divide-title mb-4">{passedItem?.product_code}</h6>

                        <div className="mc-product-view-gallery">
                            <div className="position-relative d-inline-block w-100">
                                <img
                                    src={process.env.REACT_APP_IMG_URL + currentImage}
                                    alt="product"
                                    className="main-product-image w-100"
                                />
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light border position-absolute top-0 end-0 m-2 p-1 d-flex align-items-center justify-content-center shadow-sm"
                                    style={{ borderRadius: "4px", zIndex: 5 }}
                                    onClick={() => downloadImage(currentImage)}
                                    title="Download Main Image"
                                >
                                    <i className="material-icons" style={{ fontSize: "18px" }}>
                                        file_download
                                    </i>
                                </button>
                            </div>

                            <div className="d-flex flex-row flex-wrap gap-2 mt-3">
                                {allImages.map((img, index) => (
                                    <div key={`${img}-${index}`} className="position-relative d-inline-block">
                                        <img
                                            src={process.env.REACT_APP_IMG_URL + img}
                                            alt="gallery_img"
                                            onClick={() => setSelectedImage(img)}
                                            className={`product-thumbnail ${currentImage === img ? "border-primary" : ""}`}
                                            style={{
                                                width: "60px",
                                                height: "60px",
                                                objectFit: "cover",
                                                cursor: "pointer",
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light border position-absolute top-0 end-0 m-1 p-0 d-flex align-items-center justify-content-center shadow-sm"
                                            style={{
                                                width: "18px",
                                                height: "18px",
                                                borderRadius: "3px",
                                                zIndex: 5,
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                downloadImage(img);
                                            }}
                                            title="Download"
                                        >
                                            <i className="material-icons" style={{ fontSize: "12px" }}>
                                                file_download
                                            </i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Col>

                    <Col xl={7}>
                        <h6 className="mc-divide-title mb-4">{t("product_details")}</h6>

                        <div className="mc-product-view-info-group">
                            <div className="mc-product-view-meta">
                                <i className="material-icons">title</i>
                                <h5>Product Title</h5>
                                <span>:</span>
                                <p>{passedItem?.title}</p>
                                <CopyButton value={passedItem?.title} />
                            </div>

                            <div className="mc-product-view-meta">
                                <i className="material-icons">inventory_2</i>
                                <h5>Product Code</h5>
                                <span>:</span>
                                <p>{passedItem?.product_code}</p>
                                <CopyButton value={passedItem?.product_code} />
                            </div>

                            <div className="mc-product-view-meta">
                                <i className="material-icons">category</i>
                                <h5>Category</h5>
                                <span>:</span>
                                <p>{passedItem?.category?.name}</p>
                                <CopyButton value={passedItem?.category?.name} />
                            </div>

                            <div className="mc-product-view-meta">
                                <i className="material-icons">branding_watermark</i>
                                <h5>Brand</h5>
                                <span>:</span>
                                <p>{passedItem?.brand?.name}</p>
                                <CopyButton value={passedItem?.brand?.name} />
                            </div>

                            <div className="mc-product-view-meta">
                                <i className="material-icons">sell</i>
                                <h5>Selling Style</h5>
                                <span>:</span>
                                <p>{passedItem?.selling_style}</p>
                                <CopyButton value={passedItem?.selling_style} />
                            </div>

                            <div className="mc-product-view-meta">
                                <i className="material-icons">local_shipping</i>
                                <h5>Shipping Charges</h5>
                                <span>:</span>
                                <p>${passedItem?.shipping_charges_dollar}</p>
                                <CopyButton value={`$${passedItem?.shipping_charges_dollar}`} />
                            </div>

                            <div className="mc-product-view-meta">
                                <i className="material-icons">local_shipping</i>
                                <h5>Sell Price</h5>
                                <span>:</span>
                                <p>${passedItem?.sell_price_dollar}</p>
                                <CopyButton value={`$${passedItem?.sell_price_dollar}`} />
                            </div>

                            {passedItem?.selling_style === "Auction" && (
                                <div className="mc-product-view-meta">
                                    <i className="material-icons">gavel</i>
                                    <h5>Auction Min Price</h5>
                                    <span>:</span>
                                    <p>${passedItem?.pricing?.auction_min_price ?? passedItem?.auction_min_price ?? "N/A"}</p>
                                    <CopyButton value={`$${passedItem?.pricing?.auction_min_price ?? passedItem?.auction_min_price ?? "N/A"}`} />
                                </div>
                            )}

                            {passedItem?.short_description && (
                                <div className="mt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="text-muted small d-block">
                                            {t("Short Description")}
                                        </span>
                                        <CopyButton
                                            value={htmlToText(decodeEntities(passedItem?.short_description))}
                                        />
                                    </div>
                                    <div
                                        className="text-secondary bg-light p-3 rounded mb-0 small lh-base"
                                        dangerouslySetInnerHTML={{
                                            __html: decodeEntities(passedItem?.short_description),
                                        }}
                                    />
                                </div>
                            )}

                            {productAttributes.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="mc-divide-title mb-3">{t("product attributes")}</h6>
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover align-middle mb-0">
                                            <tbody>
                                                {productAttributes.map((attribute, index) => (
                                                    <tr key={`${attribute.name}-${index}`}>
                                                        <th scope="row" className="w-50">{attribute.name}</th>
                                                        <td className="text-break">{attribute.value}</td>
                                                        <td className="text-center" style={{ width: 48 }}>
                                                            <CopyButton value={attribute.value === "—" ? "" : attribute.value} />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-4">
                            <h6 className="mc-divide-title mb-3">Product Marketplaces</h6>

                            <Row className="g-3">
                                {assignedMarketPlaces.length > 0 ? (
                                    assignedMarketPlaces.map((item) => {
                                        const fullUrl =
                                            item.type === "id"
                                                ? `${item.marketPlace?.base_url || ""}${item.reference || ""}`
                                                : item.reference;

                                        return (
                                            <Col md={12} key={item.id}>
                                                <div className="p-3 border rounded shadow-sm bg-light d-flex align-items-center justify-content-between flex-wrap gap-3">
                                                    <div
                                                        className="d-flex align-items-center gap-2"
                                                        style={{ minWidth: "160px" }}
                                                    >
                                                        {item.marketPlace?.logo && (
                                                            <img
                                                                src={process.env.REACT_APP_IMG_URL + item.marketPlace.logo}
                                                                alt="logo"
                                                                style={{ width: "24px", height: "24px", objectFit: "contain" }}
                                                            />
                                                        )}
                                                        <h6
                                                            className="mb-0 text-primary fw-bold"
                                                            style={{ fontSize: "14px" }}
                                                        >
                                                            {item.marketPlace?.name || "N/A"}
                                                        </h6>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2 flex-grow-1 text-break">
                                                        <span className="text-muted small fw-semibold">URL:</span>
                                                        {fullUrl ? (
                                                            <a
                                                                href={fullUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="small text-decoration-none fw-semibold text-primary"
                                                            >
                                                                {fullUrl}
                                                            </a>
                                                        ) : (
                                                            <span className="small text-muted">N/A</span>
                                                        )}
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2">
                                                        <CopyButton value={fullUrl} />
                                                    </div>
                                                </div>
                                            </Col>
                                        );
                                    })
                                ) : (
                                    <Col xl={12}>
                                        <p className="text-muted text-center py-2 border rounded bg-light mb-0 small">
                                            No marketplaces assigned to this product yet.
                                        </p>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    </Col>

                    <Col xl={12}>
                        <div className="description-header">
                            <h6 className="mc-divide-title mt-5 mb-4">{t("product_description")}</h6>

                            <CopyButton value={htmlToText(decodeDescription(passedItem?.description))} />
                        </div>

                        <div className="mc-product-description">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: decodeDescription(passedItem?.description),
                                }}
                            />
                        </div>
                    </Col>
                </Row>
            </div>

            <MarketPlaceModal
                show={showMarketModal}
                onHide={() => setShowMarketModal(false)}
                productId={passedItem?.id}
                onSuccess={fetchMarketPlaces}
            />
        </PageLayout>
    );
}